'use client'

import { motion } from 'framer-motion'
import Image from 'next/image'
import {useEffect, useState} from "react";
import { useMenu } from '@/contexts/MenuContext'

export default function Hero() {
	const [timeLeft, setTimeLeft] = useState({
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0
	})

	const [isHeroVisible, setIsHeroVisible] = useState(true)
	const [isMobile, setIsMobile] = useState(false)
	const [isClient, setIsClient] = useState(false)
	const { isMenuOpen } = useMenu()

	// Détecter si on est côté client
	useEffect(() => {
		setIsClient(true)
		const checkIsMobile = () => {
			setIsMobile(window.innerWidth < 768)
		}

		checkIsMobile()
		window.addEventListener('resize', checkIsMobile)
		return () => window.removeEventListener('resize', checkIsMobile)
	}, [])

	useEffect(() => {
		const weddingDate = new Date('December 5, 2025 15:00:00').getTime()

		const timer = setInterval(() => {
			const now = new Date().getTime()
			const distance = weddingDate - now

			if (distance > 0) {
				setTimeLeft({
					days: Math.floor(distance / (1000 * 60 * 60 * 24)),
					hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
					minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
					seconds: Math.floor((distance % (1000 * 60)) / 1000)
				})
			}
		}, 1000)

		return () => clearInterval(timer)
	}, [])

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				setIsHeroVisible(entry.isIntersecting)
			},
			{
				threshold: 0.1,
				rootMargin: '-10% 0px -10% 0px'
			}
		)

		const heroElement = document.querySelector('#hero-section')
		if (heroElement) {
			observer.observe(heroElement)
		}

		return () => {
			if (heroElement) {
				observer.unobserve(heroElement)
			}
		}
	}, [])

	const CountdownContent = ({ className }: { className?: string }) => (
		<div className={`flex md:grid md:grid-cols-4 justify-around md:gap-4 w-full md:max-w-md font-cormorant ${className}`}>
			<div className="text-center">
				<div className="text-2xl md:text-4xl font-bold text-white mb-1">{timeLeft.days}</div>
				<div className="text-xs md:text-base text-white/80">Jours</div>
			</div>
			<div className="text-center">
				<div className="text-2xl md:text-4xl font-bold text-white mb-1">{timeLeft.hours}</div>
				<div className="text-xs md:text-base text-white/80">Heures</div>
			</div>
			<div className="text-center">
				<div className="text-2xl md:text-4xl font-bold text-white mb-1">{timeLeft.minutes}</div>
				<div className="text-xs md:text-base text-white/80">Minutes</div>
			</div>
			<div className="text-center">
				<div className="text-2xl md:text-4xl font-bold text-white mb-1">{timeLeft.seconds}</div>
				<div className="text-xs md:text-base text-white/80">Secondes</div>
			</div>
		</div>
	)

	// Ne pas afficher le compteur flottant tant que nous ne sommes pas côté client
	if (!isClient) {
		return (
			<section
				id="hero-section"
				className="relative min-h-screen w-full flex flex-col justify-center items-center"
			>
				{/* Image de fond */}
				<Image
					src="/img/IMG_4831.JPG"
					alt="Julia & Benjamin"
					fill
					className="
	    				object-cover
	    				object-[center_40%]
	    				z-0
	  				"
					priority
					sizes="100vw"
				/>

				<div className="absolute inset-0 bg-black/30 z-10" />

				{/* Contenu haut */}
				<div className="relative z-20 flex flex-col items-center w-full md:pt-10 mb-16">
					<motion.h1
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="text-white text-4xl sm:text-5xl md:text-8xl mb-2 tracking-tight text-center font-monsieurLaDoulaise"
					>
						Julia & Benjamin
					</motion.h1>
					<div className="text-white text-2xl sm:text-3xl md:text-4xl tracking-wide text-center mb-4">
						On se dit oui !
					</div>
				</div>

				{/* Bouton */}
				<div className="absolute bottom-20 md:bottom-10 left-0 w-full flex justify-center md:justify-end pb-8 md:pb-10 z-20">
					<div className="w-full flex justify-center md:justify-end md:mr-[3.5rem]">
						<a
							href="#rsvp"
							className="
								text-center max-w-md
								px-8 py-3
								bg-transparent border border-[#b19872] text-white
								shadow-none
								hover:bg-white/10 hover:text-[#b19872]
								font-semibold
								transition-colors duration-300
								md:py-4 md:px-12
								md:rounded-[5px]
								md:text-right
								rounded-[5px]
								md:mr-16
							"
							style={{ background: undefined }}
							onClick={(e) => {
								e.preventDefault()
								document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth' })
							}}
						>
							Confirmer ma présence
						</a>
					</div>
				</div>

				{/* Countdown dans le Hero */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.6 }}
					className="absolute bottom-2 mb-5 md:bottom-10 w-full flex justify-center px-4 md:px-0"
				>
					<CountdownContent />
				</motion.div>
			</section>
		)
	}

	return (
		<>
			<section
				id="hero-section"
				className="relative min-h-screen w-full flex flex-col justify-center items-center"
			>
				{/* Image de fond */}
				<Image
					src="/img/IMG_4831.JPG"
					alt="Julia & Benjamin"
					fill
					className="
	    				object-cover
	    				object-[center_40%]
	    				z-0
	  				"
					priority
					sizes="100vw"
				/>

				<div className="absolute inset-0 bg-black/30 z-10" />

				{/* Contenu haut */}
				<div className="relative z-20 flex flex-col items-center w-full md:pt-10 mb-16">
					<motion.h1
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="text-white text-4xl sm:text-5xl md:text-8xl mb-2 tracking-tight text-center font-monsieurLaDoulaise"
					>
						Julia & Benjamin
					</motion.h1>
					<div className="text-white text-2xl sm:text-3xl md:text-4xl tracking-wide text-center mb-4">
						On se dit oui !
					</div>
				</div>

				{/* Bouton */}
				<div className="absolute bottom-20 md:bottom-10 left-0 w-full flex justify-center md:justify-end pb-8 md:pb-10 z-20">
					<div className="w-full flex justify-center md:justify-end md:mr-[3.5rem]">
						<a
							href="#rsvp"
							className="
								text-center max-w-md
								px-8 py-3
								bg-transparent border border-[#b19872] text-white
								shadow-none
								hover:bg-white/10 hover:text-[#b19872]
								font-semibold
								transition-colors duration-300
								md:py-4 md:px-12
								md:rounded-[5px]
								md:text-right
								rounded-[5px]
								md:mr-16
							"
							style={{ background: undefined }}
							onClick={(e) => {
								e.preventDefault()
								document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth' })
							}}
						>
							Confirmer ma présence
						</a>
					</div>
				</div>

				{/* Countdown dans le Hero */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.6 }}
					className="absolute bottom-2 mb-5 md:bottom-10 w-full flex justify-center px-4 md:px-0"
				>
					<CountdownContent />
				</motion.div>
			</section>

			{/* Compteur flottant qui apparaît quand le Hero n'est plus visible */}
			<motion.div
				initial={{ opacity: 0, x: -100 }}
				animate={{
					opacity: !isHeroVisible && !isMenuOpen ? 1 : 0,
					x: !isHeroVisible && !isMenuOpen ? 0 : -100
				}}
				transition={{
					duration: 0.8,
					ease: "easeInOut"
				}}
				className="fixed top-4 md:top-auto md:bottom-16 md:left-8 z-[9999] pointer-events-none"
				style={{
					display: !isHeroVisible && !isMenuOpen ? 'block' : 'none',
					left: isMobile ? '6.5rem' : undefined
				}}
			>
				<div className="rounded-lg p-2 md:p-3">
					<div className="flex gap-1 md:gap-2">
						<div className="text-center min-w-[45px]">
							<div className="text-sm md:text-lg text-[#b19872] font-cormorant">{timeLeft.days}</div>
							<div className="text-xs text-[#b19872]/70">J</div>
						</div>
						<div className="text-center min-w-[45px]">
							<div className="text-sm md:text-lg text-[#b19872] font-cormorant">{timeLeft.hours}</div>
							<div className="text-xs text-[#b19872]/70">H</div>
						</div>
						<div className="text-center min-w-[45px]">
							<div className="text-sm md:text-lg text-[#b19872] font-cormorant">{timeLeft.minutes}</div>
							<div className="text-xs text-[#b19872]/70">M</div>
						</div>
						<div className="text-center min-w-[45px]">
							<div className="text-sm md:text-lg text-[#b19872] font-cormorant">{timeLeft.seconds}</div>
							<div className="text-xs text-[#b19872]/70">S</div>
						</div>
					</div>
				</div>
			</motion.div>
		</>
	)
}
